import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
import { Calendar, MessageSquare, TrendingUp, Users, Clock, Activity, DollarSign, Download, Tag, Zap } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

// ── constants ────────────────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  'Technical':        '#0088FE',
  'Offer/Price':      '#FF6B6B',
  'Contact/Company':  '#00C49F',
  'General Info':     '#FFBB28',
  'Other':            '#8884D8',
  'Uncategorized':    '#bdbdbd',
};

const TENANT_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFBB28', '#8884D8'];

const PIE_COLORS = ['#0088FE', '#FF6B6B', '#00C49F', '#FFBB28', '#8884D8', '#82CA9D'];

const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || '#8884D8';

// ── StatCard ─────────────────────────────────────────────────────────────────

const StatCard = ({ title, value, icon: Icon, color = '#0088FE' }) => (
  <div className="stat-card" style={{ borderLeft: `4px solid ${color}` }}>
    <div className="stat-icon"><Icon size={24} color={color} /></div>
    <div className="stat-content">
      <h3>{title}</h3>
      <p className="stat-value">{value}</p>
    </div>
  </div>
);

// ── CategoryBadge ─────────────────────────────────────────────────────────────

const CategoryBadge = ({ category }) => {
  if (!category) return <span style={{ fontSize: '11px', color: '#bdbdbd' }}>—</span>;
  return (
    <span style={{
      background: getCategoryColor(category),
      color: '#fff',
      padding: '2px 8px',
      borderRadius: '10px',
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}>
      {category}
    </span>
  );
};

// ── main component ────────────────────────────────────────────────────────────

const Analytics = () => {
  const [analyticsData, setAnalyticsData] = useState({
    conversations: { daily: [], weekly: [], monthly: [] },
    session_categories: [],
    tenantStats: {},
    unmatched_questions: {},
    cost_estimation: {},
    loading: true,
  });

  const [sessionBrowser, setSessionBrowser] = useState({
    sessions: [], total: 0, loading: false, skip: 0,
  });
  const [selectedSessions, setSelectedSessions] = useState(new Set());

  const [sessionModal, setSessionModal] = useState({
    open: false, session: null, messages: [], loading: false,
  });

  const [unmatchedDetails, setUnmatchedDetails] = useState({
    fallback_responses: [], low_confidence_responses: [], validation_failures: [], loading: false,
  });

  const [selectedTenant, setSelectedTenant] = useState(
    localStorage.getItem('admin_selected_tenant_id') || 'all'
  );
  const [tenants, setTenants] = useState([]);
  const [dateRange, setDateRange] = useState('7d');
  const [exportingAll, setExportingAll] = useState(false);
  const [exportingSelected, setExportingSelected] = useState(false);
  const [categorizing, setCategorizing] = useState(false);

  // ── data fetching ───────────────────────────────────────────────────────────

  const fetchTenants = useCallback(async () => {
    try {
      const res = await axios.get('/api/tenant/');
      setTenants(res.data);
    } catch {
      toast.error('Failed to load tenants');
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setAnalyticsData(prev => ({ ...prev, loading: true }));
    try {
      const res = await axios.get('/api/admin/analytics', {
        params: { tenant_id: selectedTenant, date_range: dateRange },
      });
      setAnalyticsData({
        conversations: res.data.conversations,
        session_categories: res.data.session_categories || [],
        tenantStats: res.data.tenant_stats || {},
        unmatched_questions: res.data.unmatched_questions || {},
        cost_estimation: res.data.cost_estimation || {},
        loading: false,
      });
    } catch {
      toast.error('Failed to load analytics');
      setAnalyticsData(prev => ({ ...prev, loading: false }));
    }
  }, [selectedTenant, dateRange]);

  const fetchSessions = useCallback(async (dr, tenant, skip = 0) => {
    setSessionBrowser(prev => ({ ...prev, loading: true, skip }));
    try {
      const res = await axios.get('/api/admin/sessions', {
        params: { date_range: dr, tenant_id: tenant || 'all', skip, limit: 20 },
      });
      setSessionBrowser({ sessions: res.data.sessions, total: res.data.total, loading: false, skip });
      setSelectedSessions(new Set());
    } catch {
      toast.error('Failed to load sessions');
      setSessionBrowser(prev => ({ ...prev, loading: false }));
    }
  }, []);

  const openSession = async (session) => {
    setSessionModal({ open: true, session, messages: [], loading: true });
    try {
      const res = await axios.get(`/api/admin/sessions/${session.id}/messages`);
      setSessionModal(prev => ({ ...prev, messages: res.data.messages, loading: false }));
    } catch {
      toast.error('Failed to load messages');
      setSessionModal(prev => ({ ...prev, loading: false }));
    }
  };

  const fetchUnmatchedDetails = async () => {
    setUnmatchedDetails(prev => ({ ...prev, loading: true }));
    try {
      const res = await axios.get('/api/admin/unmatched-questions', {
        params: { tenant_id: selectedTenant, days: dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90 },
      });
      setUnmatchedDetails({
        fallback_responses: res.data.fallback_responses || [],
        low_confidence_responses: res.data.low_confidence_responses || [],
        validation_failures: res.data.validation_failures || [],
        loading: false,
      });
    } catch {
      toast.error('Failed to load unmatched details');
      setUnmatchedDetails(prev => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => { fetchTenants(); }, [fetchTenants]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);
  useEffect(() => { fetchSessions(dateRange, selectedTenant, 0); }, [fetchSessions, dateRange, selectedTenant]);

  // ── manual categorization ───────────────────────────────────────────────────

  const runCategorization = async () => {
    setCategorizing(true);
    try {
      await axios.post('/api/admin/run-categorization', null, {
        params: { tenant_id: selectedTenant },
      });
      toast.success('Categorization running in background — refresh in a moment');
      // Wait a few seconds then refresh so background job has time to process
      setTimeout(async () => {
        await fetchAnalytics();
        await fetchSessions(dateRange, selectedTenant, 0);
        setCategorizing(false);
      }, 5000);
    } catch {
      toast.error('Categorization failed');
      setCategorizing(false);
    }
  };

  // ── PDF export ──────────────────────────────────────────────────────────────

  const exportPdf = async (sessionIds = null) => {
    const isSelected = sessionIds !== null;
    if (isSelected) setExportingSelected(true);
    else setExportingAll(true);

    try {
      const params = { date_range: dateRange, tenant_id: selectedTenant };
      if (isSelected && sessionIds.length > 0) params.session_ids = sessionIds.join(',');

      const res = await axios.get('/api/admin/sessions/export', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `conversations_${dateRange}_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exported successfully');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      if (isSelected) setExportingSelected(false);
      else setExportingAll(false);
    }
  };

  // ── selection helpers ───────────────────────────────────────────────────────

  const toggleSession = (id) => {
    setSelectedSessions(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedSessions.size === sessionBrowser.sessions.length) {
      setSelectedSessions(new Set());
    } else {
      setSelectedSessions(new Set(sessionBrowser.sessions.map(s => s.id)));
    }
  };

  // ── unmatched chart data ────────────────────────────────────────────────────

  const getUnmatchedChartData = () => {
    if (selectedTenant === 'all' && analyticsData.unmatched_questions?.unmatched_breakdown_by_tenant) {
      const bd = analyticsData.unmatched_questions.unmatched_breakdown_by_tenant;
      return ['fallback', 'low_confidence', 'off_topic'].map(cat => {
        const point = { name: cat.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) };
        Object.keys(bd).forEach(tid => { point[tid] = bd[tid][cat] || 0; });
        return point;
      });
    }
    return [
      { name: 'Fallback', count: analyticsData.unmatched_questions?.fallback_count || 0, color: '#FF6B6B' },
      { name: 'Low Confidence', count: analyticsData.unmatched_questions?.low_confidence_count || 0, color: '#FFA500' },
      { name: 'Off-topic', count: analyticsData.unmatched_questions?.off_topic_count || 0, color: '#6c757d' },
    ];
  };

  // ── render ──────────────────────────────────────────────────────────────────

  if (analyticsData.loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  const tenantUnmatchedIds = analyticsData.unmatched_questions?.unmatched_breakdown_by_tenant
    ? Object.keys(analyticsData.unmatched_questions.unmatched_breakdown_by_tenant)
    : [];

  return (
    <div className="analytics">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="analytics-header">
        <h1>Analytics Dashboard</h1>
        <div className="analytics-controls">
          <select
            value={selectedTenant}
            onChange={e => {
              const v = e.target.value;
              setSelectedTenant(v);
              if (v && v !== 'all') localStorage.setItem('admin_selected_tenant_id', v);
            }}
            className="form-select"
          >
            <option value="all">All Tenants</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>

          <select value={dateRange} onChange={e => setDateRange(e.target.value)} className="form-select">
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="90d">Last 90 Days</option>
          </select>

          <button
            onClick={runCategorization}
            disabled={categorizing}
            title="Run AI categorization now for all uncategorized sessions in the current date range"
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', background: categorizing ? '#6c757d' : '#7C3AED',
              color: '#fff', border: 'none', borderRadius: '6px', cursor: categorizing ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontWeight: 600, transition: 'background 0.2s',
            }}
          >
            <Zap size={15} />
            {categorizing ? 'Categorizing…' : 'Categorize Now'}
          </button>

          <button
            onClick={() => exportPdf()}
            disabled={exportingAll}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              padding: '8px 16px', background: '#10B981', color: '#fff',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '13px', fontWeight: 600, opacity: exportingAll ? 0.7 : 1,
            }}
          >
            <Download size={15} />
            {exportingAll ? 'Exporting…' : `Export PDF (${dateRange})`}
          </button>
        </div>
      </div>

      {/* ── Stat Cards ─────────────────────────────────────────────────────── */}
      <div className="stats-grid">
        <StatCard
          title="Total Conversations"
          value={analyticsData.tenantStats.total_conversations || 0}
          icon={MessageSquare} color="#0088FE"
        />
        <StatCard
          title="Categorized Sessions"
          value={analyticsData.tenantStats.categorized_sessions || 0}
          icon={Tag} color="#00C49F"
        />
        <StatCard
          title="Avg Response Time"
          value={`${analyticsData.tenantStats.avg_response_time || 0}s`}
          icon={Clock} color="#FFBB28"
        />
        <StatCard
          title="Unmatched Responses"
          value={`${analyticsData.unmatched_questions?.unmatched_percentage || 0}%`}
          icon={Activity} color="#FF6B6B"
        />
        <StatCard
          title="Estimated Cost (GPT-5.1)"
          value={`$${(analyticsData.cost_estimation?.estimated_cost_usd || 0).toFixed(4)}`}
          icon={DollarSign} color="#10B981"
        />
      </div>

      {/* ── Session Browser ─────────────────────────────────────────────────── */}
      <div className="chart-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
          <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <MessageSquare size={18} color="#0088FE" /> Session Browser
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            {selectedSessions.size > 0 && (
              <button
                onClick={() => exportPdf([...selectedSessions])}
                disabled={exportingSelected}
                style={{
                  display: 'flex', alignItems: 'center', gap: '6px',
                  padding: '6px 14px', background: '#0088FE', color: '#fff',
                  border: 'none', borderRadius: '6px', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 600, opacity: exportingSelected ? 0.7 : 1,
                }}
              >
                <Download size={13} />
                {exportingSelected ? 'Exporting…' : `Export selected (${selectedSessions.size})`}
              </button>
            )}
            {!sessionBrowser.loading && (
              <span style={{
                background: '#e3f2fd', color: '#1565c0',
                padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: 600,
              }}>
                {sessionBrowser.total} session{sessionBrowser.total !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        </div>

        {sessionBrowser.loading && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#6c757d' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
            <p style={{ margin: 0, fontSize: '14px' }}>Loading sessions…</p>
          </div>
        )}

        {!sessionBrowser.loading && sessionBrowser.sessions.length === 0 && (
          <div style={{ textAlign: 'center', padding: '48px 0', color: '#9e9e9e' }}>
            <MessageSquare size={36} color="#e0e0e0" style={{ marginBottom: '12px' }} />
            <p style={{ margin: 0, fontSize: '14px' }}>No sessions found for this period.</p>
          </div>
        )}

        {!sessionBrowser.loading && sessionBrowser.sessions.length > 0 && (
          <>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '32px 120px 1fr 120px 52px 72px 72px 60px 28px',
              gap: '0 10px', padding: '8px 14px',
              background: '#f8f9fa', borderRadius: '8px', marginBottom: '4px',
              fontSize: '10px', fontWeight: 700, color: '#6c757d',
              textTransform: 'uppercase', letterSpacing: '0.5px',
            }}>
              <input
                type="checkbox"
                checked={selectedSessions.size === sessionBrowser.sessions.length && sessionBrowser.sessions.length > 0}
                onChange={toggleAll}
                style={{ cursor: 'pointer' }}
              />
              <span>Date &amp; Time</span>
              <span>Tenant</span>
              <span>Category</span>
              <span style={{ textAlign: 'center' }}>Msgs</span>
              <span style={{ textAlign: 'right' }}>Avg RT</span>
              <span style={{ textAlign: 'right' }}>Cost</span>
              <span style={{ textAlign: 'right' }}>Tokens</span>
              <span></span>
            </div>

            {sessionBrowser.sessions.map((s, idx) => {
              const startDt = s.created_at ? new Date(s.created_at) : null;
              const endDt = s.last_activity ? new Date(s.last_activity) : null;
              const durationMin = startDt && endDt ? Math.round((endDt - startDt) / 60000) : null;
              const msgCount = s.message_count || 0;
              const accentColor = s.category ? getCategoryColor(s.category) : (msgCount === 0 ? '#e0e0e0' : '#90CAF9');
              const isSelected = selectedSessions.has(s.id);
              const hasCost = (s.total_cost || 0) > 0;

              return (
                <div
                  key={s.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 120px 1fr 120px 52px 72px 72px 60px 28px',
                    gap: '0 10px', alignItems: 'center',
                    padding: '10px 14px', borderRadius: '8px',
                    borderLeft: `3px solid ${accentColor}`, marginBottom: '3px',
                    background: isSelected ? '#e8f4ff' : (idx % 2 === 0 ? '#fff' : '#fafbfc'),
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = '#f0f7ff'; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafbfc'; }}
                >
                  <input type="checkbox" checked={isSelected} onChange={() => toggleSession(s.id)}
                    onClick={e => e.stopPropagation()} style={{ cursor: 'pointer' }} />

                  {/* Date & Time */}
                  <div style={{ cursor: 'pointer' }} onClick={() => openSession(s)}>
                    <div style={{ fontSize: '12px', fontWeight: 600, color: '#333' }}>
                      {startDt ? startDt.toLocaleDateString('ro-RO', { day: '2-digit', month: 'short' }) : '—'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#9e9e9e' }}>
                      {startDt ? startDt.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : ''}
                      {durationMin !== null && <span style={{ marginLeft: 4 }}>· {durationMin < 1 ? '<1m' : `${durationMin}m`}</span>}
                    </div>
                  </div>

                  {/* Tenant */}
                  <div style={{ cursor: 'pointer', overflow: 'hidden' }} onClick={() => openSession(s)}>
                    <span style={{
                      background: '#e3f2fd', color: '#1565c0',
                      padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 500,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: '100%',
                    }}>
                      {s.tenant_name}
                    </span>
                  </div>

                  {/* Category */}
                  <div style={{ cursor: 'pointer' }} onClick={() => openSession(s)}>
                    <CategoryBadge category={s.category} />
                  </div>

                  {/* Messages */}
                  <div style={{ textAlign: 'center', cursor: 'pointer' }} onClick={() => openSession(s)}>
                    <span style={{
                      background: msgCount > 0 ? '#e8f5e9' : '#f5f5f5',
                      color: msgCount > 0 ? '#2e7d32' : '#9e9e9e',
                      padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: 700,
                    }}>
                      {msgCount}
                    </span>
                  </div>

                  {/* Avg Response Time */}
                  <div style={{ textAlign: 'right', cursor: 'pointer', fontSize: '12px' }} onClick={() => openSession(s)}>
                    {s.avg_response_time_s > 0
                      ? <span style={{ color: s.avg_response_time_s > 10 ? '#e57373' : '#555' }}>{s.avg_response_time_s}s</span>
                      : <span style={{ color: '#ccc' }}>—</span>}
                  </div>

                  {/* Cost */}
                  <div style={{ textAlign: 'right', cursor: 'pointer', fontSize: '12px' }} onClick={() => openSession(s)}>
                    {hasCost
                      ? <span style={{ color: '#10B981', fontWeight: 600 }}>${s.total_cost.toFixed(4)}</span>
                      : <span style={{ color: '#ccc' }}>—</span>}
                  </div>

                  {/* Tokens */}
                  <div style={{ textAlign: 'right', cursor: 'pointer', fontSize: '11px', color: '#888' }} onClick={() => openSession(s)}>
                    {s.total_tokens > 0 ? s.total_tokens.toLocaleString() : <span style={{ color: '#ccc' }}>—</span>}
                  </div>

                  <div style={{ color: '#bdbdbd', fontSize: '16px', textAlign: 'right', cursor: 'pointer' }} onClick={() => openSession(s)}>›</div>
                </div>
              );
            })}

            {/* Pagination */}
            {sessionBrowser.total > 20 && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center', alignItems: 'center' }}>
                <button
                  className="btn-secondary"
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                  disabled={sessionBrowser.skip === 0}
                  onClick={() => fetchSessions(dateRange, selectedTenant, sessionBrowser.skip - 20)}
                >← Prev</button>
                <span style={{ fontSize: '13px', color: '#6c757d', padding: '0 8px' }}>
                  {sessionBrowser.skip + 1}–{Math.min(sessionBrowser.skip + 20, sessionBrowser.total)} of {sessionBrowser.total}
                </span>
                <button
                  className="btn-secondary"
                  style={{ padding: '6px 16px', fontSize: '13px' }}
                  disabled={sessionBrowser.skip + 20 >= sessionBrowser.total}
                  onClick={() => fetchSessions(dateRange, selectedTenant, sessionBrowser.skip + 20)}
                >Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Charts ─────────────────────────────────────────────────────────── */}
      <div className="charts-grid">

        {/* Conversations Over Time */}
        <div className="chart-card">
          <h3>Conversations Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={analyticsData.conversations.daily}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="conversations" stroke="#0088FE" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Session Category Distribution */}
        <div className="chart-card">
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tag size={16} color="#00C49F" /> Session Categories
          </h3>
          {analyticsData.session_categories.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#9e9e9e', fontSize: '14px' }}>
              No categorized sessions yet.<br />
              <span style={{ fontSize: '12px' }}>Categories are assigned nightly by the AI job.</span>
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={analyticsData.session_categories}
                    cx="50%" cy="50%"
                    outerRadius={90} innerRadius={40}
                    dataKey="value" nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {analyticsData.session_categories.map((entry, i) => (
                      <Cell key={i} fill={getCategoryColor(entry.name)} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center', marginTop: '8px' }}>
                {analyticsData.session_categories.map((entry, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                    <div style={{ width: 10, height: 10, borderRadius: 2, background: getCategoryColor(entry.name) }} />
                    {entry.name}: {entry.value}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Weekly Trend */}
        <div className="chart-card">
          <h3>Weekly Conversation Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.conversations.weekly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="conversations" fill="#FFBB28" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Unmatched Questions */}
        <div className="chart-card">
          <h3>Unmatched Responses</h3>
          <ResponsiveContainer width="100%" height={260}>
            {selectedTenant === 'all' && tenantUnmatchedIds.length > 0 ? (
              <BarChart data={getUnmatchedChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                {tenantUnmatchedIds.map((tid, i) => (
                  <Bar key={tid} dataKey={tid} stackId="a" fill={TENANT_COLORS[i % TENANT_COLORS.length]} name={tid} />
                ))}
              </BarChart>
            ) : (
              <BarChart data={getUnmatchedChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count">
                  {getUnmatchedChartData().map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
          <div style={{ textAlign: 'center', fontSize: '13px', color: '#6c757d', marginTop: '8px' }}>
            {analyticsData.unmatched_questions?.total_unmatched || 0} unmatched out of {analyticsData.unmatched_questions?.total_questions || 0} questions
            ({analyticsData.unmatched_questions?.unmatched_percentage || 0}%)
          </div>
        </div>

        {/* Cost Over Time */}
        {(analyticsData.cost_estimation?.daily_costs?.length || 0) > 0 && (
          <div className="chart-card">
            <h3>Estimated Cost Over Time (USD)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analyticsData.cost_estimation.daily_costs}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip formatter={(v) => [`$${Number(v).toFixed(4)}`, 'Cost']} />
                <Line type="monotone" dataKey="cost_usd" stroke="#10B981" strokeWidth={2} name="Cost (USD)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}

      </div>

      {/* ── Unmatched Details ───────────────────────────────────────────────── */}
      <div className="unmatched-questions-section">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3>Unmatched Response Details</h3>
          <button
            onClick={fetchUnmatchedDetails}
            disabled={unmatchedDetails.loading}
            style={{
              padding: '8px 16px', background: '#007bff', color: '#fff',
              border: 'none', borderRadius: '4px', cursor: 'pointer',
            }}
          >
            {unmatchedDetails.loading ? 'Loading…' : 'Load Details'}
          </button>
        </div>

        {unmatchedDetails.fallback_responses.length > 0 && (
          <div className="unmatched-category">
            <h4 style={{ color: '#FF6B6B' }}>Fallback Responses ({unmatchedDetails.fallback_responses.length})</h4>
            <div className="table-container">
              <table>
                <thead><tr><th>Question</th><th>Response</th><th>Date</th><th>Reason</th></tr></thead>
                <tbody>
                  {unmatchedDetails.fallback_responses.map((item, i) => (
                    <tr key={i}>
                      <td>{item.question}</td>
                      <td>{item.response}</td>
                      <td>{new Date(item.timestamp).toLocaleDateString()}</td>
                      <td><span style={{ background: '#FF6B6B', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{item.reason}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {unmatchedDetails.low_confidence_responses.length > 0 && (
          <div className="unmatched-category">
            <h4 style={{ color: '#FFA500' }}>Low Confidence ({unmatchedDetails.low_confidence_responses.length})</h4>
            <div className="table-container">
              <table>
                <thead><tr><th>Question</th><th>Response</th><th>Date</th><th>Score</th></tr></thead>
                <tbody>
                  {unmatchedDetails.low_confidence_responses.map((item, i) => (
                    <tr key={i}>
                      <td>{item.question}</td>
                      <td>{item.response}</td>
                      <td>{new Date(item.timestamp).toLocaleDateString()}</td>
                      <td><span style={{ background: '#FFA500', color: '#fff', padding: '2px 8px', borderRadius: '12px', fontSize: '12px' }}>{item.confidence_score?.toFixed(2) || '—'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {unmatchedDetails.validation_failures.length > 0 && (
          <div className="unmatched-category">
            <h4 style={{ color: '#FFD700' }}>Validation Failures ({unmatchedDetails.validation_failures.length})</h4>
            <div className="table-container">
              <table>
                <thead><tr><th>Question</th><th>Response</th><th>Date</th><th>Issues</th></tr></thead>
                <tbody>
                  {unmatchedDetails.validation_failures.map((item, i) => (
                    <tr key={i}>
                      <td>{item.question}</td>
                      <td>{item.response}</td>
                      <td>{new Date(item.timestamp).toLocaleDateString()}</td>
                      <td>{item.validation_issues?.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* ── Session Modal ───────────────────────────────────────────────────── */}
      {sessionModal.open && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
          onClick={() => setSessionModal(prev => ({ ...prev, open: false }))}
        >
          <div
            style={{ background: '#fff', borderRadius: '12px', width: '100%', maxWidth: '680px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
            onClick={e => e.stopPropagation()}
          >
            {/* Modal header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #e9ecef', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <h3 style={{ margin: 0, fontSize: '16px' }}>Conversation</h3>
                  <CategoryBadge category={sessionModal.session?.category} />
                </div>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6c757d', fontFamily: 'monospace' }}>{sessionModal.session?.id}</p>
                <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6c757d' }}>
                  {sessionModal.session?.tenant_name} · {sessionModal.session?.created_at ? new Date(sessionModal.session.created_at).toLocaleString('ro-RO') : ''} · {sessionModal.session?.message_count} messages
                </p>
              </div>
              <button
                onClick={() => setSessionModal(prev => ({ ...prev, open: false }))}
                style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
              >×</button>
            </div>

            {/* Messages */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px', background: '#f8f9fa', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {sessionModal.loading && <p style={{ textAlign: 'center', color: '#6c757d' }}>Loading messages…</p>}
              {!sessionModal.loading && sessionModal.messages.map(msg => (
                <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '80%', padding: '10px 14px', borderRadius: '14px',
                    background: msg.role === 'user' ? '#007bff' : '#fff',
                    color: msg.role === 'user' ? '#fff' : '#333',
                    border: msg.role === 'user' ? 'none' : '1px solid #e9ecef',
                    fontSize: '14px', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {msg.content}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px', display: 'flex', gap: '8px' }}>
                    <span>{msg.created_at ? new Date(msg.created_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    {msg.role === 'assistant' && msg.response_time > 0 && <span>{(msg.response_time / 1000).toFixed(1)}s</span>}
                  </div>
                </div>
              ))}
              {!sessionModal.loading && sessionModal.messages.length === 0 && (
                <p style={{ textAlign: 'center', color: '#6c757d' }}>No messages in this session.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
